import { ActivityMetadataModel } from "../../../api/api";
import { uuid } from "../../../common/uuid";
import { nodeFromActivity } from "../Components/Nodes/ActivityNode";
import { nodeFromBlock } from "../Components/Nodes/BlockNode";
import { nodeFromGroup } from "../Components/Nodes/GroupNode";
import { nodeFromNote } from "../Components/Nodes/NoteNode";
import { nodeFromOnError } from "../Components/Nodes/OnErrorNode";
import { nodeFromSection } from "../Components/Nodes/SectionNode";
import { edgeFromTransition } from "../Components/Nodes/TransitionEdge";
import { ActivityTypes } from "../Components/ActivityIcon";
import { ActivityModel, FlowDefinitionModel, TransitionModel } from "../Models/FlowModel";
import { ActivityNode, FlowEdge, FlowNode } from "../Models/GraphModel";
import { isBlockToolContentHidden } from './AgentServiceCompact';

function validateEdges(edges: FlowEdge[], nodes: ActivityNode[]): FlowEdge[] {
	const invalidEdges = edges.filter((edge) => {
		// Source handle must end with '-s' (source type)
		// Target handle must end with '-t' (target type)
		const sourceHandleValid = !edge.sourceHandle || edge.sourceHandle.endsWith("-s");
		const targetHandleValid = !edge.targetHandle || edge.targetHandle.endsWith("-t");

		// Check if source and target nodes exist
		const sourceNodeExists = nodes.some((n) => n.id === edge.source);
		const targetNodeExists = nodes.some((n) => n.id === edge.target);

		return !(sourceHandleValid && targetHandleValid && sourceNodeExists && targetNodeExists);
	});

	if (invalidEdges.length > 0) {
		console.warn(
			"⚠️ Found invalid edges during API load, removing:",
			invalidEdges.map((e) => ({
				id: e.id,
				source: e.source,
				sourceHandle: e.sourceHandle,
				target: e.target,
				targetHandle: e.targetHandle,
			}))
		);
	}

	return edges.filter((edge) => !invalidEdges.some((inv) => inv.id === edge.id));
}

export default class FlowModelConverterService {
	private activityByName: Map<string, ActivityMetadataModel>;

	constructor(activityByName: Map<string, ActivityMetadataModel> = new Map()) {
		this.activityByName = activityByName;
	}

	private isBlock(type: string): boolean {
		return this.activityByName.get(type)?.isBlock === true;
	}

	private isNote(type: string): boolean {
		return type === ActivityTypes.NOTE;
	}

	private isSection(type: string): boolean {
		return type === ActivityTypes.SECTION;
	}

	private isOnError(type: string): boolean {
		return type === ActivityTypes.ON_ERROR;
	}

	private isGroupTool(type: string): boolean {
		return type === ActivityTypes.GROUP_TOOL;
	}

	private isCollapsedBlockTool(activity: ActivityModel): boolean {
		return isBlockToolContentHidden(activity);
	}

	private makeNode(a: ActivityModel): ActivityNode {
		const x = a.Metadata?.x ?? 0;
		const y = a.Metadata?.y ?? 0;
		if (this.isNote(a.Type)) return nodeFromNote(a, x, y);
		if (this.isSection(a.Type)) return nodeFromSection(a, x, y);
		if (this.isOnError(a.Type)) return nodeFromOnError(a, x, y);
		if (this.isGroupTool(a.Type)) return nodeFromGroup(a, x, y);
		return this.isBlock(a.Type) ? nodeFromBlock(a, x, y) : nodeFromActivity(a, x, y);
	}

	toDiagramModel(model: FlowDefinitionModel) {
		const nodes: ActivityNode[] = [];
		const edges: FlowEdge[] = [];

		const processActivities = (activities: ActivityModel[], parentId?: string, parentHidesChildren: boolean = false) => {
			activities.forEach((a) => {
				let node = this.makeNode(a);
				if (parentId) {
					node = { ...node, parentId };
				}
				if (parentHidesChildren) {
					node = { ...node, hidden: true };
				}
				nodes.push(node);
				a.Transitions?.forEach((t) => edges.push(edgeFromTransition(t, a)));

				// Recursively process children nested inside block nodes
				const nestedContent = a.Content as ActivityModel[] | undefined;
				if (Array.isArray(nestedContent) && nestedContent.length > 0) {
					processActivities(nestedContent, node.id, parentHidesChildren || this.isGroupTool(a.Type) || this.isCollapsedBlockTool(a));
				}
			});
		};

		processActivities(model.Content);

		// Validate and filter out invalid edges
		const validatedEdges = validateEdges(edges, nodes);

		return { nodes, edges: validatedEdges };
	}

	toFlowDefinitionModel(
		edges: FlowEdge[],
		nodes: FlowNode[],
		flowId: string | undefined,
		flowName: string,
		flowDefinition: FlowDefinitionModel | undefined
	): FlowDefinitionModel {
		const edgesBySource = new Map<string, FlowEdge[]>();
		for (const edge of edges) {
			if (!edgesBySource.has(edge.source)) { edgesBySource.set(edge.source, []); }
			edgesBySource.get(edge.source)!.push(edge);
		}
		const getActivityTransitions = (activity: ActivityModel) =>
			(edgesBySource.get(activity.Id) ?? [])
				.map(
					(e) =>
						({
							...(e.data as TransitionModel),
							Id: e.id,
							To: e.target,
							Metadata: {
								...(e.data as TransitionModel)?.Metadata,
								sourceHandle: e.sourceHandle,
								targetHandle: e.targetHandle,
							},
						}) as TransitionModel
				);

		// Build ActivityModel for every node, keyed by React Flow node id
		const activityById = new Map<string, ActivityModel>();
		nodes.forEach((n) => {
			const isBlock = this.isBlock(n.data.Type);
			const isGroupTool = this.isGroupTool(n.data.Type);
			const isSection = this.isSection(n.data.Type);
			const isNote = this.isNote(n.data.Type);
			const isResizable = isBlock || isSection || isNote;
			activityById.set(n.id, {
				...n.data,
				Metadata: {
					...n.data.Metadata,
					x: n.position.x,
					y: n.position.y,
					...(isResizable && n.data.Metadata?.compact !== true && n.width != null ? { width: n.width } : {}),
					...(isResizable && n.data.Metadata?.compact !== true && n.height != null ? { height: n.height } : {}),
				},
				Transitions: getActivityTransitions(n.data),
				...(isBlock || isGroupTool ? { Content: [] } : {}),
			});
		});

		// Nest child activities inside their parent block's Content array
		nodes.forEach((n) => {
			if (n.parentId) {
				const parentActivity = activityById.get(n.parentId);
				const childActivity = activityById.get(n.id);
				if (parentActivity && childActivity) {
					if (!parentActivity.Content) parentActivity.Content = [];
					(parentActivity.Content as ActivityModel[]).push(childActivity);
				}
			}
		});

		nodes.forEach((n) => {
			const activity = activityById.get(n.id);
			if (!activity?.Content) return;

			const nodeContent = (n.data as ActivityModel).Content;
			const contentOrder = Array.isArray(nodeContent) ? nodeContent.map((child) => child.Id) : [];
			if (contentOrder.length === 0) return;

			const orderById = new Map(contentOrder.map((childId, index) => [childId, index]));
			(activity.Content as ActivityModel[]).sort((left, right) => (orderById.get(left.Id) ?? Number.MAX_SAFE_INTEGER) - (orderById.get(right.Id) ?? Number.MAX_SAFE_INTEGER));
		});

		// Top-level content: only nodes without a parent
		const topLevelContent = nodes.filter((n) => !n.parentId).map((n) => activityById.get(n.id)!);

		const newFlowDefinition: FlowDefinitionModel = {
			Id: flowId ?? uuid(),
			Name: flowName,
			SchemaValues: flowDefinition?.SchemaValues,
			InputSchema: flowDefinition?.InputSchema,
			OutputSchema: flowDefinition?.OutputSchema,
			InputSchemaValue: flowDefinition?.InputSchemaValue,
			OutputSchemaValue: flowDefinition?.OutputSchemaValue,
			Content: topLevelContent,
			RuntimeSettings: {
				StoreTransitions: flowDefinition?.RuntimeSettings?.StoreTransitions,
				TraceEnabled: flowDefinition?.RuntimeSettings?.TraceEnabled,
				StoreVariables: flowDefinition?.RuntimeSettings?.StoreVariables,
				DeleteAfter: flowDefinition?.RuntimeSettings?.DeleteAfter,
				AttributeCaptures: flowDefinition?.RuntimeSettings?.AttributeCaptures,
			},
		};

		return newFlowDefinition;
	}
}
